import { DataGrid, DataGridColumn, DataGridRef, SkeletonLoader } from '@cimplico/components';
import { ROW_SIZES } from '../../constants';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { useJournals } from '../../queries/use-journals';
import { JournalOverview } from '../../types/journal';
import { Ref } from 'react';
import JournalRow from './JournalRow';
import EmptyStateNew from '../../components/empty-state/EmptyStateNew';
import NoResults from '../../components/empty-state/NoResults';
import { TableCellsIcon } from '@heroicons/react/24/outline';

const columns: DataGridColumn[] = [
    {
        header: 'Journal',
        alignment: 'left',
        width: '2fr',
    },
    {
        header: 'Debit',
        alignment: 'right',
        width: '0.5fr',
    },
    {
        header: 'Credit',
        alignment: 'right',
        width: '0.5fr',
    },
    {
        header: 'Status',
        alignment: 'center',
        width: '0.5fr',
    },
];

type JournalsTableProps = {
    search?: string;
    sort?: string;
    filter?: string;
    searchFilters?: string;
    disablePagination?: boolean;
    tableRef: Ref<DataGridRef> | null;
    onSelect: (journals: JournalOverview[]) => void;
    onClick?: (journalId: string) => void;
    handleNewJournal: () => void;
};

function JournalsTable({
    search,
    sort,
    filter,
    searchFilters,
    disablePagination,
    tableRef,
    onClick,
    onSelect,
    handleNewJournal,
}: JournalsTableProps): JSX.Element {
    const { id, readOnly } = useWorkpaperContext();

    const journalsQuery = useJournals(id, { search, sort, filter, searchFilters });

    function selectJournals(ids: string[]): void {
        const selectedJournals = journalsQuery.data?.journals?.filter((j) => ids.includes(j.id)) ?? [];
        onSelect(selectedJournals);
    }

    if (journalsQuery.isLoading) {
        return <SkeletonLoader width="100%" height="600px" />;
    }

    if (!journalsQuery.isSuccess) {
        return <></>;
    }

    const { journals, totalItems } = journalsQuery.data;

    if (journals === undefined || totalItems === undefined) {
        return <></>;
    }

    const isSearching = Boolean(search);

    return (
        <div className="relative">
            <DataGrid
                ref={tableRef}
                columns={columns}
                rowSizes={ROW_SIZES}
                totalRows={totalItems}
                isLoading={journalsQuery.isFetching}
                emptyMessage={
                    isSearching ? (
                        <NoResults search={search || ''} />
                    ) : (
                        <EmptyStateNew
                            icon={<TableCellsIcon />}
                            actionText="New Journal"
                            helpLink="https://learn.cimplico.com/docs/journals-overview#creating-a-journal"
                            helpText="Draft and post journals within Cimplico Workpapers."
                            title="Draft a journal"
                            onClick={handleNewJournal}
                            disableActionButton={readOnly}
                        />
                    )
                }
                disablePagination={disablePagination}
                onSelect={selectJournals}
            >
                {journals.map((journal) => {
                    return <JournalRow id={journal.id} key={journal.id} journal={journal} onClick={onClick} />;
                })}
            </DataGrid>
        </div>
    );
}

export default JournalsTable;
